import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import meta from "@/locales/meta.json";
import en from "@/locales/en.json";
import hi from "@/locales/hi.json";
import ta from "@/locales/ta.json";
import ar from "@/locales/ar.json";
import fr from "@/locales/fr.json";
import { readPref, saveLocalePref, setCountryRegistry } from "@/utils/formatters";

/**
 * I18nContext — versioned frontend locale files (src/locales) + regional preferences.
 *
 * Language precedence: `?lang=` → explicit choice (account `explicit` flag or device `f2n_lang`)
 * → browser languages (navigator.languages) → English. Never inferred from IP or GPS.
 * Country is independent of language: explicit choice → "IN" default (never auto-switched).
 * Lookup chain for t(): current locale → English → caller fallback → key. Only languages with a
 * locale file are offered; `status` (supported/preview) + `reviewed` come from locales/meta.json.
 */
const I18nContext = createContext(null);
const DICTS = { en, hi, ta, ar, fr };
export const LANGS = meta.languages;
const FALLBACK = meta.fallback;
const SUPPORTED = Object.keys(LANGS);
// Script direction safety net for any RTL locale file added later (ur planned) even if meta omits `dir`.
const RTL_LANGS = new Set(["ar", "ur", "he", "fa", "sd", "ks"]);

export const matchLang = (code) => {
  const base = String(code || "").toLowerCase().split(/[-_]/)[0];
  return DICTS[base] ? base : null;
};

function browserLang() {
  try {
    for (const l of navigator.languages || [navigator.language]) { const m = matchLang(l); if (m) return m; }
  } catch { /* SSR */ }
  return null;
}

function pickInitial() {
  try {
    const q = matchLang(new URLSearchParams(window.location.search).get("lang"));
    if (q) return { lang: q, source: "url" };
    const ls = matchLang(localStorage.getItem("f2n_lang"));
    if (ls) return { lang: ls, source: "explicit" };
  } catch { /* storage blocked */ }
  const b = browserLang();
  return b ? { lang: b, source: "browser" } : { lang: FALLBACK, source: "default" };
}

function promptSync(code) {
  toast.info(`Language changed while offline (${LANGS[code]?.native || code}) is not saved to your account.`, {
    id: "f2n-lang-unsynced", duration: 20000,
    action: { label: "Save now", onClick: () => api.patch("/me/language", { language: code })
      .then(() => localStorage.removeItem("f2n_lang_unsynced")).catch(() => toast.error("Still offline — try again later")) },
  });
}

const interpolate = (s, vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s);

function useRegion() {
  const pref = readPref();
  const [country, setCountryState] = useState((pref.country || "IN").toUpperCase());
  const [tz, setTzState] = useState(pref.tz || null);
  const [currencyDisplay, setCurrencyDisplayState] = useState(pref.currency_display || "source");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/regions/countries").then((r) => {
      setCountryRegistry(r.data?.countries);
      setCountries(r.data?.countries || []);
    }).catch(() => {});
    api.get("/me/regional").then((r) => {
      const p = r.data?.prefs || {};
      if (!r.data?.explicit) return;
      if (p.country) setCountryState(p.country);
      if (p.timezone) setTzState(p.timezone);
      if (p.currency_display) setCurrencyDisplayState(p.currency_display);
      saveLocalePref({ country: p.country, tz: p.timezone, currency_display: p.currency_display });
    }).catch(() => { /* guest */ });
  }, []);

  const persist = (patch, local) => {
    saveLocalePref(local);
    api.patch("/me/regional", patch).catch(() => { /* guest or offline: device-only */ });
  };
  const setCountry = (c) => {
    const info = countries.find((x) => x.code === c);
    setCountryState(c);
    setTzState(info?.default_timezone || null);
    persist({ country: c, ...(info && { timezone: info.default_timezone }) }, { country: c, tz: info?.default_timezone });
  };
  const setTimezone = (z) => { setTzState(z); persist({ timezone: z }, { tz: z }); };
  const setCurrencyDisplay = (v) => { setCurrencyDisplayState(v); persist({ currency_display: v }, { currency_display: v }); };
  const region = countries.find((x) => x.code === country) || null;
  return { country, setCountry, timezone: tz || region?.default_timezone || "Asia/Kolkata", setTimezone,
           currencyDisplay, setCurrencyDisplay, countries, region };
}

export function I18nProvider({ children }) {
  const [{ lang, source }, setPick] = useState(pickInitial);
  const region = useRegion();
  const info = LANGS[lang] || LANGS[FALLBACK];

  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("lang", lang);
    el.setAttribute("dir", RTL_LANGS.has(lang) ? "rtl" : info.dir);
    el.dataset.localeStatus = info.status;
  }, [lang, info]);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/language").then((r) => {
      const m = matchLang(r.data?.language);
      if (cancelled || !r.data?.explicit || !m) return;
      // A device choice made while offline was never sent: keep it and ask before saving (explicit retry).
      if (localStorage.getItem("f2n_lang_unsynced") === "1") {
        const dev = matchLang(localStorage.getItem("f2n_lang"));
        if (dev && dev !== m) promptSync(dev);
        else localStorage.removeItem("f2n_lang_unsynced");
        return;
      }
      if (new URLSearchParams(window.location.search).get("lang")) return;
      setPick({ lang: m, source: "account" });
      try { localStorage.setItem("f2n_lang", m); } catch { /* noop */ }
    }).catch(() => { /* guest */ });
    return () => { cancelled = true; };
  }, []);

  const setLang = useCallback((next) => {
    const code = matchLang(next) || FALLBACK;
    setPick({ lang: code, source: "explicit" });
    try {
      localStorage.setItem("f2n_lang", code);
      localStorage.setItem("f2n_lang_prompt_seen", "1");
    } catch { /* noop */ }
    saveLocalePref({ lang: code });
    api.patch("/me/language", { language: code })
      .then(() => { try { localStorage.removeItem("f2n_lang_unsynced"); } catch { /* noop */ } })
      .catch((e) => {
        if (e?.code === "F2N_OFFLINE_WRITE_BLOCKED") { try { localStorage.setItem("f2n_lang_unsynced", "1"); } catch { /* noop */ } }
      });
  }, []);

  const strings = DICTS[lang];
  const t = useCallback((key, fallback, vars) => {
    if (fallback && typeof fallback === "object") { vars = fallback; fallback = undefined; }
    const s = strings[key] ?? en[key] ?? fallback ?? key;
    return interpolate(s, vars);
  }, [strings]);

  const value = useMemo(() => ({
    lang, source, setLang, t, strings, info, languages: LANGS, supported: SUPPORTED,
    loading: false, version: meta.version, ...region,
  }), [lang, source, setLang, t, strings, info, region]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

/** Rendered inside the Router (Layout) so alternate links follow SPA navigation. */
export function HreflangLinks() {
  const { lang } = useI18n();
  const { pathname, search } = useRouterLocation();
  useEffect(() => {
    const head = document.head;
    head.querySelectorAll("link[data-f2n-hreflang]").forEach((n) => n.remove());
    const supported = SUPPORTED.filter((c) => LANGS[c].status === "supported");
    if (!supported.includes(lang)) return;   // preview locales get no localized SEO metadata
    for (const c of [...supported, "x-default"]) {
      const u = new URL(window.location.href);
      u.searchParams.set("lang", c === "x-default" ? FALLBACK : c);
      const l = document.createElement("link");
      Object.assign(l, { rel: "alternate", href: u.toString() });
      l.setAttribute("hreflang", c);
      l.setAttribute("data-f2n-hreflang", "1");
      head.appendChild(l);
    }
  }, [lang, pathname, search]);
  return null;
}
